import { create } from 'axios';
import { type Feature } from 'geojson';

import { getCurrentPlatformTheme } from '@/helpers/platform';
import {
  type Culture,
  KpiTypeValue,
  type LevelIdValue,
  type LevelValue,
  PropertyType,
  type PropertyTypeValue,
} from '@/models/iazi/common/enums';
import { type CantonPolygonFullResponse } from '@/models/iazi/queries/getCantonPolygonFullResponse';
import { type PriceDistributionResponse } from '@/models/iazi/queries/getPriceDistributionResponse';
import { type PriceEvolutionResponse } from '@/models/iazi/queries/getPriceEvolutionResponse';
import { type PriceRoomDistributionResponse } from '@/models/iazi/queries/getPriceRoomDistributionResponse';
import { type PriceValueResponse } from '@/models/iazi/queries/getPriceValueResponse';
import { captureError } from '@smg-real-estate/browser-rum';

const instance = create({
  baseURL: import.meta.env.VUE_MARKET_ATLAS_API_URL,
});

export default instance;

const DEFAULT_CULTURE: Culture = 'de-CH';

interface BasicProps {
  level: LevelValue;
  levelId: LevelIdValue;
  category: PropertyTypeValue;
  culture: Culture;
}

interface PriceDistributionProps extends Omit<BasicProps, 'levelId'> {
  levelId?: number;
}
/**
 * @param Level - 1- (CH), 2-(Canton), 3- MS region, 5-(municipality), 6- town
 * @param kpiType - 1- price, 2 - Price/m2, 3- rent, 4 - Rent/m2,
 * @param category - level "A2" = house,  "A3" = condominium
 * @param parentId - is optional. Ex: For municipality, parentId = cantoncode. Not passing it will return all polygons inside switzerland
 */
export const getPriceDistribution = async ({
  level,
  category,
  culture = DEFAULT_CULTURE,
  levelId,
}: PriceDistributionProps): Promise<PriceDistributionResponse> => {
  try {
    const colorId = getCurrentPlatformTheme();
    const path = encodeURIComponent(`/v1/pricevaluedistribution`);
    const queryParams = `Level=${level}&Culture=${culture}&CountryCode=CH&&ColorId=${colorId}&Category=${category}`;
    const parentParam = levelId ? `&LevelId=${levelId}` : '';
    const url = `?path=${path}&queryParams=${encodeURIComponent(queryParams + parentParam)}`;

    const response = await instance.get(url);

    return response.data;
  } catch (error) {
    captureError(error);
    return null;
  }
};

interface PriceLevelsProps extends Omit<BasicProps, 'category'> {
  kpiType?: KpiTypeValue;
}
/**
 * @param kpiType - 1- price, 2 - Price/m2, 3- rent, 4 - Rent/m2,
 * @param Level - 1- (CH), 2-(Canton), 3- MS region, 5-(municipality), 6- town
 * @param levelId - level === 1 = "country",  level === 2 = "canton", level === 5 ="municipality", level === 6 = "town"
 */
export const getPriceLevels = async ({
  kpiType = KpiTypeValue.PricePerSquareMeter,
  level,
  levelId,
  culture = DEFAULT_CULTURE,
}: PriceLevelsProps): Promise<PriceValueResponse> => {
  try {
    const path = encodeURIComponent(`/v1/pricevalue`);
    const queryParams = encodeURIComponent(
      `KpiType=${kpiType}&Level=${level}&LevelId=${levelId}&Culture=${culture}`,
    );

    const response = await instance.get(`?path=${path}&queryParams=${queryParams}`);

    return response.data;
  } catch (error) {
    captureError(error);
    return null;
  }
};
export const getPriceRoomDistribution = async ({
  level,
  levelId,
}: BasicProps): Promise<PriceRoomDistributionResponse> => {
  try {
    const path = encodeURIComponent(`/v1/priceroomdistribution`);
    const queryParams = encodeURIComponent(`Level=${level}&LevelId=${levelId}`);

    const response = await instance.get(`?path=${path}&queryParams=${queryParams}`);

    return response.data;
  } catch (error) {
    captureError(error);
    return {
      results: {
        roomsListApartment: null,
        roomsListHouse: null,
        [PropertyType.Apartment]: null,
        [PropertyType.House]: null,
      },
    };
  }
};

interface PriceEvolutionProps extends BasicProps {
  yearRange?: number;
}

export const getPriceEvolution = async ({
  level,
  levelId,
  yearRange = 4,
}: PriceEvolutionProps): Promise<PriceEvolutionResponse> => {
  try {
    const path = encodeURIComponent(`/v1/priceevolution`);
    const queryParams = encodeURIComponent(
      `Level=${level}&LevelId=${levelId}&NumberOfYears=${yearRange}`,
    );

    // NOTE:: category, yearRange and culture are optional parameter. Not recommended to pass if not necessary.
    const response = await instance.get(`?path=${path}&queryParams=${queryParams}`);

    return response.data;
  } catch (error) {
    captureError(error);
    return null;
  }
};

export const getPolygon = async ({
  level,
  levelId,
  culture = DEFAULT_CULTURE,
}: {
  level: LevelValue;
  levelId: LevelIdValue;
  culture?: Culture;
}): Promise<Feature | null> => {
  try {
    const path = encodeURIComponent(`/v1/polygons`);
    const queryParams = encodeURIComponent(
      `LevelId=${levelId}&Culture=${culture}&CountryCode=CH&Level=${level}`,
    );

    const response = await instance.get(`?path=${path}&queryParams=${queryParams}`);

    const data: CantonPolygonFullResponse = response.data as CantonPolygonFullResponse;
    return data?.results?.features?.[0] ?? null;
  } catch (error) {
    captureError(error);
    return null;
  }
};
